/**
 * 公共机翻按钮（2026-09-20 后台自动翻译英文初稿，06 模块《后台自动翻译_实现说明》§2.3）。
 *
 * 「整块翻译」形态：一次把当前页全部中文字段送 POST /admin/translate/draft，
 * 结果按同顺序回填英文输入框（可编辑草稿）。调用方传 getter 取最新中文值、
 * onBeforeApply 判断覆盖确认（返回 false 取消）、onApply 回填。
 *
 * 交互契约（方案 §3 验收）：
 * - 覆盖确认：目标英文区已有内容 → window.confirm「将覆盖已填英文」（onBeforeApply 返回 false 取消）；全空直接填。
 * - 「机翻初稿」小标：onApply 后由调用方置脏标记；用户编辑字段后自行清标记（onFieldEdit 由输入框 onChange 调）。
 * - 失败不阻塞手输：行内错误提示，按钮恢复。
 * - MT_DISABLED（本地 dev）：mtEnabled=false → 按钮禁用 + tooltip 说明。
 */
import { useState } from 'react';
import { adminHttp } from '../lib/api';
import { Button } from '@chronospeak/ui';
import type { TranslateDraftRequest, TranslateDraftResponse } from '@chronospeak/contracts';

export function TranslateButton({
  mtEnabled,
  collectSources,
  onBeforeApply,
  onApply,
  label = '从中文翻译（机翻初稿）',
}: {
  /** 会话级机翻开关（/admin/me mtEnabled；本地 dev false → 禁用态） */
  mtEnabled: boolean;
  /** 收集当前中文原文（按字段顺序；空段自动跳过，单段 ≤5000 字符服务端校验） */
  collectSources: () => string[];
  /** 发送前调用：返回 false 取消（覆盖确认等场景） */
  onBeforeApply?: (sources: string[]) => boolean;
  /** 回填英文区（translated 与 sources 一一对应）+ 置「机翻初稿」脏标记 */
  onApply: (translated: string[]) => void;
  label?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    if (busy) return;
    setError(null);
    const sources = collectSources().map((s) => s.trim()).filter(Boolean);
    if (sources.length === 0) {
      setError('中文内容为空，没有可翻译的文本。');
      return;
    }
    if (onBeforeApply && !onBeforeApply(sources)) return;
    setBusy(true);
    try {
      const res = await adminHttp.post<TranslateDraftResponse>('/admin/translate/draft', {
        texts: sources,
        targetLocale: 'en',
      } satisfies TranslateDraftRequest);
      onApply(res.items.map((i) => i.translated));
    } catch (e) {
      setError(e instanceof Error ? e.message : '翻译失败，请稍后重试或手输英文。');
    } finally {
      setBusy(false);
    }
  };

  if (!mtEnabled) {
    return (
      <span title='机器翻译未启用（本地开发默认关闭）'>
        <Button variant='ghost' size='sm' disabled>
          {label}
        </Button>
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <Button variant='ghost' size='sm' onClick={() => void run()} disabled={busy}>
        {busy ? '翻译中…' : label}
      </Button>
      {error && (
        <span role='alert' style={{ color: '#e8a9a0', fontSize: 13 }}>
          {error}
        </span>
      )}
    </span>
  );
}

/** 「机翻初稿」小标（填充后显示；用户编辑后由调用方隐藏） */
export function MachineDraftBadge({ visible }: { visible: boolean }) {
  if (!visible) return null;
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 12,
        lineHeight: 1.6,
        padding: '0 6px',
        borderRadius: 4,
        border: '1px dashed var(--line-strong)',
        color: 'var(--ink-soft)',
        verticalAlign: 'middle',
      }}
    >
      机翻初稿
    </span>
  );
}
